import { useId, useState, type KeyboardEvent } from 'react';
import { INTERVENTION_MAX_LEN, searchInterventions } from '../lib/interventionSearch.js';

/**
 * ehr#571 (PROGRESS_NOTE_10-20-V141 V127): "Search or add an intervention" under Interventions used.
 * Typing lists the practice's interventions not ticked yet; a click (or Enter) ticks one. Something
 * not on the list can be added: it is ticked, and the parent puts it on the practice list.
 */
export function InterventionSearchBox({
  list,
  ticked,
  hidden = [],
  onTick,
  onAdd,
}: {
  list: readonly string[];
  ticked: readonly string[];
  /** ehr#682 (V141 V127): hidden for this client; offered first, tagged "Hidden for this client". */
  hidden?: readonly string[];
  onTick: (item: string) => void;
  onAdd: (text: string) => void;
}) {
  const id = useId();
  const [q, setQ] = useState('');
  const [hl, setHl] = useState(0);
  const r = searchInterventions(list, ticked, q, hidden);
  const options: { key: string; label: string; tag?: string; run: () => void }[] = [
    ...r.matches.map((m) => ({
      key: m,
      label: m,
      ...(hidden.includes(m) ? { tag: 'Hidden for this client' } : {}),
      run: () => onTick(m),
    })),
    ...(r.add ? [{ key: '+add', label: `+ Add "${r.add}"`, run: () => onAdd(r.add!) }] : []),
  ];
  const done = (run: () => void) => {
    run();
    setQ('');
    setHl(0);
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!options.length) return;
      e.preventDefault();
      setHl((h) => (h + (e.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // already on the list but not offered (typed exactly): tick it
      if (r.exact && !ticked.includes(r.exact) && !options.length)
        return done(() => onTick(r.exact!));
      const o = options[Math.min(hl, options.length - 1)];
      if (o) done(o.run);
    } else if (e.key === 'Escape' && q) {
      e.preventDefault();
      e.stopPropagation();
      setQ('');
    }
  };
  const open = q.trim().length > 0;
  return (
    <div className="pn-iv-search">
      <input
        type="text"
        value={q}
        maxLength={80}
        placeholder="Search or add an intervention"
        aria-label="Search or add an intervention"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-list`}
        aria-autocomplete="list"
        onChange={(e) => {
          setQ(e.target.value);
          setHl(0);
        }}
        onKeyDown={onKey}
      />
      {open && (
        <ul className="pn-iv-list" role="listbox" id={`${id}-list`}>
          {options.length ? (
            options.map((o, i) => (
              <li key={o.key} role="option" aria-selected={i === hl}>
                <button
                  type="button"
                  className={`pn-iv-opt${o.key === '+add' ? ' add' : ''}${i === hl ? ' hl' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => done(o.run)}
                >
                  {o.label}
                  {o.tag && <span className="pn-iv-tag">{o.tag}</span>}
                </button>
              </li>
            ))
          ) : (
            <li className="pn-iv-none" role="option" aria-selected={false} aria-disabled>
              {q.trim().length > INTERVENTION_MAX_LEN
                ? 'Nothing to add: 60 characters at most.'
                : 'Already ticked.'}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
